import type { SessionExerciseStatus } from './enums.js';

/**
 * Completed-exercise-first ordering for the active workout screen (Phase 3).
 *
 * Pure and framework-free, like every other domain module here — the
 * display-only reordering belongs beside `previous-performance.ts`/
 * `rest-timer.ts`, not inside the React component, so it can be tested
 * directly and reused by a future native client.
 */

/**
 * Whether an exercise counts as complete for ordering purposes only — never
 * written back to `SessionExerciseRecord.status`, which stays exactly what
 * it already was (Phase 0's own `'pending'`/`'skipped'` reality; `'active'`/
 * `'completed'` are never actually assigned anywhere in this codebase).
 *
 * `liveRows` is whatever this exercise's own live (non-tombstoned) rows are
 * — `SessionSetRecord[]` for strength/hold, `CardioSegmentRecord[]` for
 * cardio. Both already share the one field this needs, `isCompleted`, so one
 * function serves both without duplicating the same "every live row done"
 * check per modality.
 *
 * A skipped exercise never counts as complete here regardless of what its
 * rows show — it stays with the unfinished group, per the product rule.
 * An exercise with zero live rows never counts as complete either:
 * `liveRows.every(...)` is vacuously `true` on an empty array, which would
 * otherwise put a not-yet-materialised or fully-removed exercise at the
 * front of the list for the wrong reason.
 */
export function isExerciseCompleteForOrdering(
  status: SessionExerciseStatus,
  liveRows: readonly { isCompleted: boolean }[],
): boolean {
  if (status === 'skipped') return false;
  return liveRows.length > 0 && liveRows.every((row) => row.isCompleted);
}

/**
 * Stable completed-first partition: every item `isComplete` returns `true`
 * for comes first, in its original relative order, followed by the rest, in
 * their original relative order. Never sorts by anything else (not
 * alphabetical, not by when something became complete), and never mutates
 * `items` — the caller's own array (however it got it, e.g. straight from a
 * repository read) is read-only to this function.
 */
export function stablePartitionCompletedFirst<T>(
  items: readonly T[],
  isComplete: (item: T) => boolean,
): T[] {
  const completed: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    (isComplete(item) ? completed : rest).push(item);
  }
  return [...completed, ...rest];
}
